import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { mailboxApi, usersApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/components/SimpleToast";

const PRIOS = ["Niedrig", "Mittel", "Hoch"];
const intro = (e) => `Aus E-Mail „${e.subject}“ von ${e.from_name || e.from_email}${e.excerpt ? `:\n${e.excerpt}` : ""}`;

export function TaskFromEmailDialog({ email: e, topicName, open, onOpenChange, onCreated }) {
  const { user } = useAuth();
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ title: e.subject || "", description: intro(e), assigned_to_user_id: "", due_date: "", priority: "Mittel", notify_email: true });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  useEffect(() => { usersApi.getAll().then((r) => setUsers((r.data || []).filter((u) => u.is_active !== false))).catch(() => {}); }, []);

  const submit = async () => {
    if (!f.title.trim() || !f.assigned_to_user_id) { toast.error("Bitte Titel und Kollegen wählen"); return; }
    setBusy(true);
    try {
      await mailboxApi.createTask(e.id, { ...f, due_date: f.due_date || null });
      toast.success("Aufgabe erstellt – Mail ist jetzt „in Bearbeitung“");
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Aufgabe konnte nicht erstellt werden");
    } finally { setBusy(false); }
  };

  const ctx = [e.status === "assigned" && e.target_name, topicName].filter(Boolean).join(" · ");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="mail-task-dialog">
        <DialogHeader>
          <DialogTitle>Aufgabe aus E-Mail</DialogTitle>
          <DialogDescription>{ctx ? `Verknüpft mit: ${ctx}` : "Diese Mail ist noch keiner Praxis zugeordnet."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Titel</Label><Input value={f.title} onChange={set("title")} data-testid="mail-task-title" /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Für Kollegen</Label>
              <Select value={f.assigned_to_user_id} onValueChange={set("assigned_to_user_id")}>
                <SelectTrigger data-testid="mail-task-assignee"><SelectValue placeholder="Bitte wählen" /></SelectTrigger>
                <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id} data-testid={`mail-task-assignee-${u.id}`}>{u.full_name}{u.id === user?.id ? " (ich)" : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Fällig am</Label><Input type="date" value={f.due_date} onChange={set("due_date")} data-testid="mail-task-due" /></div>
          </div>
          <div className="space-y-1.5">
            <Label>Priorität</Label>
            <Select value={f.priority} onValueChange={set("priority")}>
              <SelectTrigger data-testid="mail-task-priority"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIOS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Beschreibung</Label><Textarea rows={4} value={f.description} onChange={set("description")} data-testid="mail-task-description" /></div>
          <label className="flex items-center justify-between gap-3 text-sm">
            <span>Kollegen zusätzlich per E-Mail informieren</span>
            <Switch checked={f.notify_email} onCheckedChange={set("notify_email")} data-testid="mail-task-notify-email" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="mail-task-cancel">Abbrechen</Button>
          <Button onClick={submit} disabled={busy} data-testid="mail-task-submit">{busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Aufgabe erstellen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
